import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from 'barte-design-system';
import { TrashIcon, XIcon } from '@phosphor-icons/react';

import type { ILinkAssignment } from '../../terminal-operation-drawer.type';

import { useStepLinkConfigData } from './use-step-link-config-data';
import styles from './step-link-config.module.css';
import type { IStepLinkConfigProps } from './step-link-config.type';

export const StepLinkConfig = ({
  allSerialNumbers,
  selectedPayfac,
  linkAssignments,
  onPayfacChange,
  onAssignmentsChange,
  isSubmitting,
  showSellerSection = true,
}: IStepLinkConfigProps) => {
  const [sellerSearch, setSellerSearch] = useState('');
  const { payfacs, isPayfacsLoading, sellers } = useStepLinkConfigData(selectedPayfac?.id, sellerSearch);
  const { t } = useTranslation();

  const assignedSerials = useMemo(
    () => new Set(linkAssignments.flatMap((a) => a.serialNumbers)),
    [linkAssignments],
  );

  const unassignedSerials = useMemo(
    () => allSerialNumbers.filter((s) => !assignedSerials.has(s)),
    [allSerialNumbers, assignedSerials],
  );

  const availableSellers = useMemo(() => {
    const addedIds = new Set(linkAssignments.map((a) => a.sellerId).filter(Boolean));
    return sellers.filter((s) => !addedIds.has(s.id));
  }, [sellers, linkAssignments]);

  const totalAssigned = assignedSerials.size;
  const total = allSerialNumbers.length;
  const allAllocated = unassignedSerials.length === 0 && total > 0;

  const handleSelectPayfac = (value: string) => {
    const payfac = payfacs.find((p) => String(p.id) === value);
    if (!payfac) return;
    onPayfacChange(payfac);
    onAssignmentsChange([]);
    setSellerSearch('');
  };

  const shouldAutoAssignSingleSerial =
    allSerialNumbers.length === 1 && unassignedSerials.length === 1;

  const handleAddSeller = (value: string) => {
    const seller = sellers.find((s) => String(s.id) === value);
    if (!seller) return;
    const newAssignment: ILinkAssignment = {
      uid: String(seller.id),
      sellerId: seller.id,
      sellerName: seller.name,
      serialNumbers: shouldAutoAssignSingleSerial ? [...unassignedSerials] : [],
    };
    onAssignmentsChange([...linkAssignments, newAssignment]);
  };

  const handleRemoveSeller = (uid: string) => {
    onAssignmentsChange(linkAssignments.filter((a) => a.uid !== uid));
  };

  const handleAddSerial = (uid: string, serial: string) => {
    const assignment = linkAssignments.find((a) => a.uid === uid);
    if (!assignment || assignment.serialNumbers.includes(serial)) return;
    const updated = linkAssignments.map((a) =>
      a.uid === uid ? { ...a, serialNumbers: [...a.serialNumbers, serial] } : a,
    );
    onAssignmentsChange(updated);
  };

  const handleRemoveSerial = (uid: string, serial: string) => {
    const updated = linkAssignments.map((a) =>
      a.uid === uid ? { ...a, serialNumbers: a.serialNumbers.filter((s) => s !== serial) } : a,
    );
    onAssignmentsChange(updated);
  };

  return (
    <div className={styles.container}>
      <div className={styles.section}>
        <span className={styles['section-title']}>
          {t('backoffice.terminalsDrawerStepLinkConfigPayfacTitle')}
        </span>
        <span className={styles['section-description']}>
          {t('backoffice.terminalsDrawerStepLinkConfigPayfacDescription')}
        </span>

        <Select
          searchable
          filterLocally
          hidePrefixIcon
          disabled={isSubmitting || isPayfacsLoading}
          placeholder={t('backoffice.terminalsDrawerStepLinkConfigPayfacPlaceholder')}
          value={selectedPayfac ? String(selectedPayfac.id) : ''}
          onChange={handleSelectPayfac}
          options={payfacs.map((p) => ({ value: String(p.id), label: p.name }))}
        />
      </div>

      {selectedPayfac && showSellerSection && (
        <div className={styles.section}>
          <span className={styles['section-title']}>
            {t('backoffice.terminalsDrawerStepLinkConfigDistributionTitle')}
          </span>
          <span className={styles['section-description']}>
            {t('backoffice.terminalsDrawerStepLinkConfigDistributionDescription1')}
            <strong>{t('backoffice.terminalsDrawerStepLinkConfigDistributionStatusLabel')}</strong>
            {t('backoffice.terminalsDrawerStepLinkConfigDistributionDescription2')}
          </span>

          <Select
            searchable
            filterLocally={false}
            hidePrefixIcon
            disabled={isSubmitting}
            placeholder={t('backoffice.terminalsDrawerStepLinkConfigSellerTrigger')}
            value=""
            onChange={handleAddSeller}
            onSearch={setSellerSearch}
            options={availableSellers.map((s) => ({ value: String(s.id), label: s.name }))}
          />

          {linkAssignments.length > 0 && (
            <div className={styles['allocation-row']}>
              <span className={allAllocated ? styles['counter-allocated'] : styles['counter-partial']}>
                {totalAssigned}/{total} {t('backoffice.terminalsDrawerStepLinkConfigCounterLabel')}
              </span>
              {unassignedSerials.length > 0 && (
                <span className={styles['unallocated-badge']}>
                  {t('backoffice.terminalsDrawerStepLinkConfigNotAllocated', {
                    count: unassignedSerials.length,
                  })}
                </span>
              )}
            </div>
          )}

          {linkAssignments.length > 0 && (
            <div className={styles['sellers-table']}>
              {linkAssignments.map((assignment) => (
                <div key={assignment.uid} className={styles['seller-row']}>
                  <div className={styles['seller-info']}>
                    <span className={styles['seller-name']}>{assignment.sellerName}</span>
                    {assignment.sellerId !== undefined && (
                      <span className={styles['seller-id']}>ID {assignment.sellerId}</span>
                    )}
                  </div>

                  <div className={styles['seller-serials']}>
                    {assignment.serialNumbers.map((serial) => (
                      <span key={serial} className={styles['serial-chip']}>
                        {serial}
                        <button
                          type="button"
                          className={styles['serial-chip-remove']}
                          onClick={() => handleRemoveSerial(assignment.uid, serial)}
                          disabled={isSubmitting}
                        >
                          <XIcon size={10} weight="bold" />
                        </button>
                      </span>
                    ))}

                    {unassignedSerials.length > 0 && (
                      <Select
                        searchable
                        filterLocally
                        hidePrefixIcon
                        disabled={isSubmitting}
                        placeholder={`+ ${t('backoffice.terminalsDrawerStepLinkConfigAddTerminal')}`}
                        value=""
                        onChange={(serial) => handleAddSerial(assignment.uid, serial)}
                        options={unassignedSerials.map((s) => ({ value: s, label: s }))}
                      />
                    )}
                  </div>

                  <button
                    type="button"
                    className={styles['seller-remove']}
                    onClick={() => handleRemoveSeller(assignment.uid)}
                    disabled={isSubmitting}
                    aria-label={t('backoffice.terminalsDrawerStepLinkConfigRemoveSeller')}
                  >
                    <TrashIcon size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
